import ServiceEndpoints from "./ServiceEndpoints";
import { fetchWithAuth, getOriginalFetch } from "../utils/fetchUtils";
import { triggerMaintenanceMode } from "../context/MaintenanceContext";

// Define the shape of the app settings
export interface SanitizationSettings {
  invalidCharactersPattern: string;
  invalidCharactersRegexTimeoutMs: number;
  throwOnInvalidInput: boolean;
  enabledRegexFilter: boolean;
}

export interface AppSettings {
  sanitization: SanitizationSettings;
  helpManualUrl?: string;
}

// Function to fetch app settings from the API
export async function fetchAppSettings(token?: string): Promise<AppSettings | null> {
  try {
    // Check if settings are already in sessionStorage
    const cachedSettings = sessionStorage.getItem('appSettings');
    if (cachedSettings) {
      return JSON.parse(cachedSettings);
    }

    // If not in sessionStorage, fetch from API with authentication if token is available
    const response = await fetchWithAuth(ServiceEndpoints.FetchAppSettings, {}, token);

    if (!response.ok) {
      throw new Error(`Failed to fetch app settings: ${response.status}`);
    }

    const data: AppSettings = await response.json();

    // Store in sessionStorage
    sessionStorage.setItem('appSettings', JSON.stringify(data));

    return data;
  } catch (err) {
    console.error('Error fetching app settings:', err);
    return null;
  }
}

// Function to get app settings from sessionStorage
export function getAppSettings(): AppSettings | null {
  const cachedSettings = sessionStorage.getItem('appSettings');
  if (cachedSettings) {
    return JSON.parse(cachedSettings);
  }
  return null;
}

// Function to validate input against invalid characters
export function validateInput(input: string): boolean {
  const settings = getAppSettings();
  if (!settings?.sanitization?.enabledRegexFilter) {
    return true;
  }

  try {
    const { invalidCharactersPattern } = settings.sanitization;
    const regex = new RegExp(invalidCharactersPattern);
    return !regex.test(input);
  } catch (error) {
    console.error('Error validating input:', error);
    return true; // Allow the request to proceed if regex fails
  }
}

// Function to extract form data from various request body types
export function extractFormData(body: any): Record<string, string> {
  if (!body) return {};

  // Handle FormData
  if (body instanceof FormData) {
    const result: Record<string, string> = {};
    body.forEach((value, key) => {
      if (typeof value === 'string') {
        result[key] = value;
      }
    });
    return result;
  }

  // Handle URLSearchParams
  if (body instanceof URLSearchParams) {
    const result: Record<string, string> = {};
    body.forEach((value, key) => {
      result[key] = value;
    });
    return result;
  }

  // Handle plain objects (for JSON)
  if (typeof body === 'object') {
    try {
      return JSON.parse(JSON.stringify(body));
    } catch {
      return {};
    }
  }

  // Handle string (for JSON)
  if (typeof body === 'string') {
    try {
      return JSON.parse(body);
    } catch {
      return {};
    }
  }

  return {};
}

// Function to extract and format invalid characters from the pattern
export function getFormattedInvalidCharacters(): string {
  const settings = getAppSettings();
  if (!settings?.sanitization?.enabledRegexFilter) {
    return "special characters like '<', '>', '&', '(', ')' and '%'";
  }

  try {
    const { invalidCharactersPattern } = settings.sanitization;
    const charactersMatch = invalidCharactersPattern?.match(/\[(.*?)\]/);

    if (charactersMatch && charactersMatch[1]) {
      // Process the matched characters
      const chars = charactersMatch[1].replaceAll(/\\(.)/g, '$1'); // Handle escaped characters like \(

      // Format the characters list with proper grammar
      const charArray = [...new Set(chars.split(''))]; // Remove duplicates

      if (charArray.length === 1) {
        return `'${charArray[0]}'`;
      } else if (charArray.length === 2) {
        return `'${charArray[0]}' and '${charArray[1]}'`;
      } else if (charArray.length > 2) {
        const lastChar = charArray.pop();
        return charArray.map(c => `'${c}'`).join(', ') + ` and '${lastChar}'`;
      }
    }
  } catch (error) {
    console.error('Error formatting invalid characters:', error);
  }

  return "special characters like '<', '>', '&', '(', ')' and '%'";
}

// Function to validate all string values in an object
export function validateObject(obj: Record<string, any>): boolean {
  for (const key in obj) {
    const value = obj[key];

    // Recursively check nested objects
    if (value && typeof value === 'object') {
      if (!validateObject(value)) {
        return false;
      }
    }

    // Check string values
    if (typeof value === 'string' && value.trim() !== '') {
      if (!validateInput(value)) {
        return false;
      }
    }
  }

  return true;
}

// Create a wrapped fetch function that validates input
export async function fetchWithValidation(input: RequestInfo | URL, init?: RequestInit): Promise<Response> {
  const settings = getAppSettings();

  // Use the original fetch to avoid circular reference
  const originalFetch = getOriginalFetch();

  // Extract URL from input to check if it's the UpdateMailBody endpoint
  let url = '';
  if (typeof input === 'string') {
    url = input;
  } else if (input instanceof URL) {
    url = input.toString();
  } else if (input instanceof Request) {
    url = input.url;
  }

  // Skip validation for the UpdateMailBody endpoint
  if (url.includes('/api/admin/Reminder/UpdateMailBody')) {
    const response = await originalFetch(input, init);
    return checkMaintenanceMode(response);
  }

  // Skip validation if settings aren't loaded or sanitization is disabled
  if (!settings?.sanitization?.enabledRegexFilter) {
    const response = await originalFetch(input, init);
    return checkMaintenanceMode(response);
  }

  const { throwOnInvalidInput } = settings.sanitization;

  // Extract and validate request body if it exists
  if (init?.body) {
    const formData = extractFormData(init.body);
    const isValid = validateObject(formData);

    if (!isValid && throwOnInvalidInput) {
      // Generate a more detailed error message using the getFormattedInvalidCharacters function
      const invalidChars = getFormattedInvalidCharacters();
      const errorMessage = `Illegal HTML content - please remove any of the following special characters: ${invalidChars}`;

      const errorResponse = new Response(
        JSON.stringify({
          errors: [errorMessage],
          data: null
        }),
        {
          status: 400,
          headers: { 'Content-Type': 'application/json' }
        }
      );
      return errorResponse;
    }
  }

  // Proceed with the fetch request using the original fetch and check for maintenance mode
  const response = await originalFetch(input, init);
  return checkMaintenanceMode(response);
}

// Function to check if response indicates maintenance mode and trigger maintenance display
function checkMaintenanceMode(response: Response): Response {
  // Get the maintenance status code from environment variable, default to 423
  const maintenanceStatusCode = import.meta.env.VITE_MAINTENANCE_STATUS_CODE 
    ? Number.parseInt(import.meta.env.VITE_MAINTENANCE_STATUS_CODE as string, 10) 
    : 423;

  // Check if the response status matches the maintenance status code
  if (response.status === maintenanceStatusCode) {
    // Trigger maintenance mode without changing the URL
    triggerMaintenanceMode(true);
  }

  return response;
}

// Initialize app settings when the application starts
export function initAppSettings(token?: string): void {
  fetchAppSettings(token).catch(console.error);
}
